import { DRIVING_GROUPS, TELEMATICS_REPORT_LIMITS, checkReportRange, daysInRange, type DrivingGroup, type LiftMetrics, type LiftResult } from "@fts/core";
import { HelpTip } from "../../components/HelpTip";
import { EmptyState, PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { descendantIds } from "../../lib/fleet";
import { coverageOf } from "../../lib/report-figures";
import { durationLabel } from "../../lib/report-sheets";
import { navigate, type Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { ChartPanel, CoverageNote, PrintBrand, ReportContext, ReportEmpty, SummaryCards, periodText, previousOf } from "./ReportParts";
import { toSummaryLines } from "./presenters";
import { ExportBar, FilterBar, Loading, contextChips, filterSummary, filtersQuery, parseFilters, rangeKey, settleWithin, siteLabel, todayLocal, useLatest, useLoad, PREVIOUS_WAIT_MS, type ExportSpec } from "./shared";
import { liftChart, liftRowLabel, liftSheet, liftSummary } from "./telematics";

/**
 * Direk (asansör) raporu (ADR 0034): lift count, lift time and "lift while driving" (the mast up while the
 * speed is above the vehicle's threshold) per vehicle or operator, from the analog lift signal with
 * hysteresis set in the vehicle's calibration. Counting only: nothing blocks driving or lifting (ADR 0003).
 * Operators follow the driving report's KVKK rules (purpose on screen; a single hidden operator is counted only).
 */
export function LiftReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale, lang } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const group: DrivingGroup = route.query.get("grp") === "operator" ? "operator" : "vehicle";
  const issue = checkReportRange(f.from, f.to, TELEMATICS_REPORT_LIMITS.maxDays);
  const tenantId = tenant?.id;
  const inSite = f.siteId ? descendantIds(data.sites, f.siteId) : null;
  const scopeVehicles = data.vehicles.filter((v) => (!inSite || inSite.has(v.siteId)) && (f.vehicleIds.length === 0 || f.vehicleIds.includes(v.id)));
  const tooLarge = !issue && scopeVehicles.length * daysInRange(f.from, f.to) > TELEMATICS_REPORT_LIMITS.maxVehicleDays;
  const query = (id: string, range = { from: f.from, to: f.to }) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, operatorId: f.operatorId, ...range, group });
  const deps = [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.operatorId, f.from, f.to, group];
  const state = useLoad<LiftResult>(tenantId && !issue && !tooLarge ? () => provider.reportLift(query(tenantId)) : null, deps);
  const prevRange = previousOf(f);
  const prev = useLoad<LiftResult>(tenantId && !issue && !tooLarge && state.status === "ready" ? () => provider.reportLift(query(tenantId, prevRange)) : null, [...deps, state.status, state.data]);
  const prevLatest = useLatest(prev);
  const res = state.data;
  const ctx = { t, locale, lang };
  const filters = filterSummary(t, data, f);
  const keep = { grp: group === "vehicle" ? undefined : group };
  const setGroup = (g: DrivingGroup) => navigate("/reports/lift", { ...filtersQuery(f), grp: g === "vehicle" ? undefined : g });
  const byOp = group === "operator";

  const spec: ExportSpec | null = state.status === "ready" && res && tenantId
    ? {
        report: "lift",
        reportName: `${t("lift.title")} — ${t(`reports.driving.group.${group}`)}`,
        slug: t("lift.slug"),
        part: `${group}_${f.from}_${f.to}`,
        tz: res.tz,
        filters: [[t("reports.driving.group"), t(`reports.driving.group.${group}`)], ...filters, ...(byOp ? [[t("reports.driving.purposeLabel"), t("lift.purpose")] as [string, string]] : [])],
        period: periodText(t, lang, f.from, f.to),
        rows: res.rows.length + 1,
        load: async (format) => {
          const [r, p] = await Promise.all([provider.exportLift(query(tenantId), format), format === "pdf" ? settleWithin(prevLatest.current.promise, PREVIOUS_WAIT_MS) : Promise.resolve(null)]);
          return { sheets: [liftSheet(t, r)], tz: r.tz, summary: toSummaryLines(ctx, liftSummary(ctx, r, p)), chart: { title: t("lift.chart.title"), spec: liftChart(ctx, r), note: t("lift.chart.note") } };
        },
      }
    : null;

  const rk = issue === "too_long" ? "lift.rangeTooLong" : rangeKey(issue);
  const cells = (m: LiftMetrics) => (
    <>
      <td className="num">{m.lifts.toLocaleString(locale)}</td>
      <td className="num">{durationLabel(t, m.liftS)}</td>
      <td className="num">{m.whileDriving.toLocaleString(locale)}</td>
      <td className="num col-hide-sm">{durationLabel(t, m.whileDrivingS)}</td>
    </>
  );

  return (
    <>
      <PrintBrand title={t("lift.title")} tz={res?.tz ?? null} />
      <PageHeader
        title={t("lift.title")}
        subtitle={t("lift.subtitle")}
        eyebrow={t("nav.group.reports")}
        help={
          <HelpTip topic={t("help.lift.topic")} align="start">
            {t("help.lift")}
          </HelpTip>
        }
      />
      {byOp && (
        <p className="kvkk-note" role="note">
          {t("lift.kvkkNote")}
        </p>
      )}
      <div className="heatmap-mode">
        <span className="field-label" id="lift-group-label">
          {t("reports.driving.group")}
        </span>
        <div className="segmented" role="group" aria-labelledby="lift-group-label">
          {[...DRIVING_GROUPS].reverse().map((g) => (
            <button key={g} type="button" className={g === group ? "seg-on" : ""} aria-pressed={g === group} onClick={() => setGroup(g)}>
              {t(`reports.driving.group.${g}`)}
            </button>
          ))}
        </div>
      </div>
      <FilterBar data={data} path="/reports/lift" filters={f} keep={keep} />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      {tooLarge && (
        <p className="form-error" role="alert">
          {t("battery.tooLarge", { max: TELEMATICS_REPORT_LIMITS.maxVehicleDays.toLocaleString(locale) })}
        </p>
      )}
      <ReportContext chips={[[t("reports.driving.group"), t(`reports.driving.group.${group}`)], ...contextChips(t, filters)]} from={f.from} to={f.to} tz={res?.tz ?? null} sample={provider.mode === "demo"} />
      {state.status === "ready" && res && res.rows.length > 0 && <SummaryCards items={liftSummary(ctx, res, prev.status === "ready" ? prev.data : null)} busy={prev.status === "loading"} prevFrom={prevRange.from} prevTo={prevRange.to} />}
      {state.status === "ready" && <CoverageNote coverage={coverageOf({ vehicles: scopeVehicles, devices: data.devices, from: f.from, to: f.to, tz: res?.tz ?? "Europe/Istanbul", today: todayLocal() })} tz={res?.tz ?? "Europe/Istanbul"} />}
      {res && res.unconfigured.length > 0 && (
        <p className="info-banner">
          {t("lift.unconfigured", { count: res.unconfigured.length, vehicles: res.unconfigured.slice(0, 6).map((u) => u.fleetNo).join(", ") + (res.unconfigured.length > 6 ? "…" : "") })}
        </p>
      )}
      <ExportBar spec={spec} disabled={!res || res.rows.length === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {state.status === "ready" && res && res.rows.length > 0 && <ChartPanel title={t("lift.chart.title")} spec={liftChart(ctx, res)} note={t("lift.chart.note")} />}
      <Panel flush meta={state.status === "ready" && res ? <span>{t("reports.rows", { count: res.rows.length })}</span> : undefined}>
        {state.status === "loading" || state.status === "idle" ? (
          rk || tooLarge ? <EmptyState compact icon="info" title={t(rk ?? "reports.driving.narrow")} /> : <Loading />
        ) : !res || res.rows.length === 0 ? (
          state.status === "error" ? null : <ReportEmpty path="/reports/lift" filters={f} siteName={siteLabel(t, data, f.siteId)} keep={keep} />
        ) : (
          <div className="table-wrap table-scroll">
            <table className="table report-table">
              <thead>
                <tr>
                  <th scope="col">{byOp ? t("reports.col.operator") : t("reports.col.vehicle")}</th>
                  <th scope="col" className="num">{t("lift.col.lifts")}</th>
                  <th scope="col" className="num">{t("lift.col.liftTime")}</th>
                  <th scope="col" className="num">{t("lift.col.whileDriving")}</th>
                  <th scope="col" className="num col-hide-sm">{t("lift.col.whileDrivingTime")}</th>
                </tr>
              </thead>
              <tbody>
                {res.rows.map((r) => (
                  <tr key={`${r.kind}:${r.id ?? ""}`}>
                    <td>
                      {r.kind === "vehicle" && r.id ? (
                        <a className="subtle-link mono" href={`#/vehicles/${encodeURIComponent(r.id)}`}>
                          {r.name}
                        </a>
                      ) : r.kind === "operator" && r.id ? (
                        <a className="subtle-link" href={`#/operators/${encodeURIComponent(r.id)}`}>
                          {r.name}
                        </a>
                      ) : (
                        <span className="muted">{liftRowLabel(t, r)}</span>
                      )}
                    </td>
                    {r.suppressed ? (
                      <td className="muted" colSpan={4}>
                        {t("reports.driving.suppressed")}
                      </td>
                    ) : (
                      cells(r)
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">{t("reports.totals")}</th>
                  {cells(res.totals)}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>
      <p className="panel-footnote">{t("lift.rules")}</p>
    </>
  );
}
